/**
 * Reverse-geocoding helper backed by OpenStreetMap Nominatim.
 *
 * Nominatim's usage policy allows at most 1 request per second and asks for
 * in-app caching. We enforce both here:
 *
 *   - results are kept in a module-level Map keyed by (lat,lng) rounded to
 *     5 decimals (~1.1 m precision), so identical lookups are instant;
 *   - all outgoing requests are serialized through a single promise chain
 *     with a 1.1 s minimum spacing, regardless of how many callers ask at
 *     the same time.
 *
 * Responses are reduced to a short, human-readable Swedish label like
 * "Löfströms allé 12, Sundbyberg" — the point of this in the admin panel
 * is to skim a stop list, not to read a full postal address.
 */

const cache = new Map();
const inFlight = new Map();

let lastCallAt = 0;
const MIN_SPACING_MS = 1100;

const coordKey = (lat, lng) =>
  `${Number(lat).toFixed(5)},${Number(lng).toFixed(5)}`;

const waitForSlot = async () => {
  const now = Date.now();
  const wait = Math.max(0, lastCallAt + MIN_SPACING_MS - now);
  if (wait > 0) {
    await new Promise((resolve) => setTimeout(resolve, wait));
  }
  lastCallAt = Date.now();
};

const formatLabel = (data) => {
  if (!data) return null;
  const address = data.address || {};

  const street = [address.road, address.house_number]
    .filter(Boolean)
    .join(" ");

  const area =
    address.suburb ||
    address.neighbourhood ||
    address.quarter ||
    address.city_district ||
    address.village ||
    null;

  const city =
    address.city || address.town || address.municipality || address.county || null;

  const parts = [];
  if (street) parts.push(street);
  if (area && area !== street) parts.push(area);
  if (city && !parts.includes(city)) parts.push(city);

  const label = parts.join(", ").trim();
  if (label) return label;

  // Last-ditch fallback: truncate the full display_name so the UI isn't
  // ever forced to show raw coordinates.
  if (data.display_name) {
    const short = data.display_name.split(",").slice(0, 3).join(",").trim();
    return short || data.display_name;
  }
  return null;
};

/**
 * Look up a street-level label for a single coordinate. Returns `null` on
 * any failure so callers can decide whether to fall back to lat/lng.
 */
export const reverseGeocode = (lat, lng) => {
  if (typeof lat !== "number" || typeof lng !== "number") {
    return Promise.resolve(null);
  }
  const key = coordKey(lat, lng);
  if (cache.has(key)) return Promise.resolve(cache.get(key));
  if (inFlight.has(key)) return inFlight.get(key);

  const promise = (async () => {
    await waitForSlot();
    try {
      const url =
        "https://nominatim.openstreetmap.org/reverse" +
        `?format=jsonv2&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1&accept-language=sv`;
      const res = await fetch(url, {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error(`reverseGeocode HTTP ${res.status}`);
      const data = await res.json();
      const label = formatLabel(data);
      cache.set(key, label);
      return label;
    } catch (err) {
      // Cache the failure too, so we don't hammer Nominatim on retries
      // within the same session.
      cache.set(key, null);
      return null;
    } finally {
      inFlight.delete(key);
    }
  })();

  inFlight.set(key, promise);
  return promise;
};

/**
 * Key helper so consumers can look up results in a labels object with the
 * exact same rounding the cache uses internally.
 */
export const labelKey = coordKey;
